import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import ReadReceiptSchema from "messaging/read-receipts/_schemas/read-receipt-schema";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import CommentThreadSchema from "work/proposals/comments/threads/_schemas/comment-thread-schema";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { z } from "zod";

export type CommentJSON = z.infer<typeof CommentSchema.CommentJSONZ>;
export type CommentParams = z.infer<typeof CommentSchema.CommentZ>;

export default class CommentSchema {
  public static readonly CommentJSONZ = z.object({
    id: z.uuid(),
    senderId: z.uuid().nullish(),
    thread: CommentThreadSchema.CommentThreadJSONZ,
    content: z.string(),
    created: z.iso.datetime({ offset: true }),
    published: z.iso.datetime({ offset: true }).nullish(),
    issues: z.array(z.string()).nullish(),
    readReceipts: z.array(ReadReceiptSchema.ReadReceiptJSONZ).nullish(),
    deleted: z.iso.datetime({ offset: true }).nullish(),
    updated: z.iso.datetime({ offset: true }).nullish(),
    isAutoGenerated: z.boolean().nullish(),
    markedForCreation: z.boolean().nullish(),
    markedForPublish: z.boolean().nullish(),
    markedForEdit: z.boolean().nullish(),
    markedForReadReceipt: z.boolean().nullish(),
    markedForUnread: z.boolean().nullish(),
    markedForDeletion: z.boolean().nullish(),
  });

  public static readonly CommentZ = z.object({
    id: z.instanceof(Guid).optional(),
    senderId: z.instanceof(Guid).optional().nullable(),
    thread: CommentThreadSchema.CommentThreadZ,
    content: z.string().optional(),
    subject: z.string().optional(),
    readReceipts: z.array(z.instanceof(ReadReceipt)).optional(),
    issues: z.array(z.string()).optional(),
    isAutoGenerated: z.boolean().optional(),
    created: DateSchema.type.optional(),
    deleted: DateSchema.type.optional(),
    updated: DateSchema.type.optional(),
    published: DateSchema.type.optional(),
    markedForCreation: z.boolean().optional(),
    markedForPublish: z.boolean().optional(),
    markedForEdit: z.boolean().optional(),
    markedForReadReceipt: z.boolean().optional(),
    markedForUnread: z.boolean().optional(),
    markedForDeletion: z.boolean().optional(),
  });

  public static validate(values: unknown): CommentParams {
    return parseOrThrow(CommentSchema.CommentZ, values, {
      logError: true,
      schemaName: "CommentSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): CommentJSON {
    return parseOrThrow(CommentSchema.CommentJSONZ, json, {
      logError: true,
      schemaName: "CommentSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is CommentParams {
    try {
      CommentSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is CommentJSON {
    try {
      CommentSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: CommentJSON): CommentParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(CommentSchema.CommentJSONZ, json, {
      logError: true,
      schemaName: "CommentSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return CommentSchema.codec.decode(parsed);
  }

  public static serialize(params: CommentParams): CommentJSON {
    if (isNullOrUndefined(params)) return params;
    if(!params.content) throw new Error("Comment.Serialize(): Content is required");
    const parsed = CommentSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return CommentSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(CommentSchema.CommentJSONZ, CommentSchema.CommentZ, {
    decode: (commentJSON) => ({
      id: Guid.fromJSON(commentJSON.id) ?? undefined,
      senderId: Guid.fromJSON(commentJSON.senderId) ?? undefined,
      thread: new CommentThread(CommentThreadSchema.deserialize(commentJSON.thread)),
      content: z.string().min(1).parse(commentJSON.content),
      created: new AHDate(DateSchema.deserialize(commentJSON.created)),
      published: AHDate.fromJSON(commentJSON.published) ?? undefined,
      readReceipts:
        commentJSON.readReceipts?.map((receipt) => new ReadReceipt(ReadReceiptSchema.deserialize(receipt))) ?? [],
      issues: commentJSON.issues ?? [],
      deleted: AHDate.fromJSON(commentJSON.deleted) ?? undefined,
      updated: AHDate.fromJSON(commentJSON.updated) ?? undefined,
      isAutoGenerated: commentJSON.isAutoGenerated ?? false,
      markedForCreation: commentJSON.markedForCreation ?? false,
      markedForPublish: commentJSON.markedForPublish ?? false,
      markedForEdit: commentJSON.markedForEdit ?? false,
      markedForReadReceipt: commentJSON.markedForReadReceipt ?? false,
      markedForUnread: commentJSON.markedForUnread ?? false,
      markedForDeletion: commentJSON.markedForDeletion ?? false,
    }),
    encode: (commentParams) => {
      if (!commentParams.id) throw new Error("Comment.Serialize(): Id is required");
      if (!commentParams.created) throw new Error("Comment.Serialize(): Created is required");
      if(!commentParams.content) throw new Error("Comment.Serialize(): Content is required");
      return {
        id: commentParams.id.toJSON(),
        senderId: commentParams.senderId?.toJSON(),
        thread: CommentThreadSchema.serialize(commentParams.thread),
        content: commentParams.content,
        created: commentParams.created.toJSON(),
        deleted: commentParams.deleted?.toJSON() ?? undefined,
        updated: commentParams.updated?.toJSON() ?? undefined,
        published: commentParams.published?.toJSON() ?? undefined,
        issues: commentParams.issues ?? [],
        readReceipts: commentParams.readReceipts?.map((receipt) => ReadReceiptSchema.serialize(receipt)) ?? [],
        isAutoGenerated: commentParams.isAutoGenerated ?? false,
        markedForCreation: commentParams.markedForCreation ?? false,
        markedForPublish: commentParams.markedForPublish ?? false,
        markedForEdit: commentParams.markedForEdit ?? false,
        markedForReadReceipt: commentParams.markedForReadReceipt ?? false,
        markedForUnread: commentParams.markedForUnread ?? false,
        markedForDeletion: commentParams.markedForDeletion ?? false,
      };
    },
  });
}
